import { cloneDeep } from 'lodash';

const changeUploadParticipantMessage =
  'Are you sure you want to switch to another input method? Unsaved changes will be lost.';

export const findNode = (nodes, id) => {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }
    if (node.children?.length > 0) {
      const foundNode = findNode(node.children, id);
      if (foundNode) {
        return foundNode;
      }
    }
  }
  return null;
};

export const saveNodeFieldsChanges = (data, nodeId, fieldsState) => {
  const tree = cloneDeep(data);
  const node = findNode(tree, nodeId);
  if (node) {
    Object.assign(node, {
      ...fieldsState,
      parentGroupName: node.parentGroupName,
    });

    node.children.forEach((child) =>
      Object.assign(child, { parentGroupName: fieldsState.name })
    );
  }

  return tree;
};

export const moveNodeToParent = (data, nodeId, fieldsState, newParentId) => {
  const tree = cloneDeep(data);
  const node = findNode(tree, nodeId);

  if (!node) {
    return tree;
  }

  Object.assign(node, fieldsState);

  const oldParent = findNode(tree, node.parentId);

  const removeNodeFromOldParent = (parent) => {
    if (parent) {
      const indexToRemove = parent.children.findIndex(
        (child) => child.id === nodeId
      );
      if (indexToRemove !== -1) {
        return parent.children.splice(indexToRemove, 1)[0];
      }
    } else {
      const indexToRemove = tree.findIndex((item) => item.id === nodeId);
      if (indexToRemove !== -1) {
        return tree.splice(indexToRemove, 1)[0];
      }
    }
    return null;
  };

  const insertNodeIntoNewParent = (newParent) => {
    if (!newParent.children) {
      newParent.children = [];
    }
    newParent.children.push(node);
  };

  removeNodeFromOldParent(oldParent);
  node.parentId = newParentId;
  const newParent = findNode(tree, newParentId);
  node.parentGroupName = newParent ? newParent.name : null;

  if (newParent) {
    insertNodeIntoNewParent(newParent);
  } else {
    tree.push(node);
  }

  return tree;
};

export const removeNode = (data, nodeId) => {
  const tree = cloneDeep(data);
  const nodeToRemove = findNode(tree, nodeId);

  if (!nodeToRemove) {
    return tree;
  }

  const parentOfNodeToRemove = findNode(tree, nodeToRemove.parentId);

  if (parentOfNodeToRemove) {
    const nodeToRemoveChildren = nodeToRemove.children;
    const indexToRemove = parentOfNodeToRemove.children.findIndex(
      (child) => child.id === nodeId
    );
    if (indexToRemove !== -1) {
      parentOfNodeToRemove.children.splice(indexToRemove, 1);
    }
    parentOfNodeToRemove.children.splice(
      indexToRemove,
      0,
      ...nodeToRemoveChildren
    );

    nodeToRemoveChildren.forEach((node) => {
      node.parentId = parentOfNodeToRemove.id;
      node.parentGroupName = parentOfNodeToRemove.parentGroupName;
    });
  } else {
    const indexToRemove = tree.findIndex((item) => item.id === nodeId);

    if (indexToRemove !== -1) {
      const removedNode = tree.splice(indexToRemove, 1)[0];

      tree.splice(indexToRemove, 0, ...removedNode.children);

      removedNode.children.forEach((node) => {
        node.parentId = null;
        node.parentGroupName = null;
      });
    }
  }

  return tree;
};

export const addNode = (data, parentId) => {
  const tree = cloneDeep(data);
  const temporaryId =
    Math.floor(Math.random() * (9999999 - 1000000 + 1)) + 1000000;

  const createNewNode = (parentName, parentId) => {
    return {
      id: temporaryId,
      destroyable: true,
      name: '',
      parentGroupName: parentName || null,
      parentId: parentId || null,
      email: '',
      isNew: true,
      children: [],
    };
  };

  if (!parentId) {
    tree.push(createNewNode());

    return { tree, temporaryId };
  }

  const parent = findNode(tree, parentId);

  if (!parent) {
    return tree;
  }

  if (!parent.children) {
    parent.children = [];
  }

  parent.children.unshift(createNewNode(parent.name, parentId));

  return { tree, temporaryId };
};

export const findDuplicateIds = (arr, propertyName, checkCase) => {
  const propertyMap = {};
  const duplicateIds = [];

  arr.forEach((item) => {
    const { id } = item;
    let propertyValue = item[propertyName];

    if (propertyValue) {
      propertyValue = checkCase ? propertyValue.toLowerCase() : propertyValue;

      if (propertyValue in propertyMap) {
        duplicateIds.push(id);
        duplicateIds.push(propertyMap[propertyValue]);
      } else {
        propertyMap[propertyValue] = id;
      }
    }
  });

  return duplicateIds;
};

export const validateGroups = (groups) => {
  const errors = {};

  const nonUniqueNameFieldsIds = findDuplicateIds(groups, 'name', false);
  const nonUniqueEmailFieldsIds = findDuplicateIds(groups, 'email', true);

  nonUniqueNameFieldsIds.forEach((id) => {
    errors[`name${id}`] = 'Duplicate Name';
  });

  nonUniqueEmailFieldsIds.forEach((id) => {
    errors[`email${id}`] = 'Duplicate Email';
  });
  return errors;
};

export const onParticipantsUploadChange = (method, setUploadMethod) => {
  if (window.confirm(changeUploadParticipantMessage)) {
    setUploadMethod(method);
  }
};
